import React, { useEffect, useState, useMemo } from 'react';
import PageShell from '../components/PageShell';
import ViewSwitch from '../components/ViewSwitch';
import { usePersistentValue } from '../hooks/usePersistentState';
import { useP } from '../i18n/useP';
import BalanceSheetPage from './BalanceSheetPage';
import ForecastBalanceSheetTab from './ForecastBalanceSheetTab';

/* Bilan — écran unique, même grammaire que le compte de résultat (2026-08-10) :
   la VUE (Réel | Prévisionnel) sur le filet, l'unité à droite, le MODE dessous.

     · Présenter   — lecture : bilan réel, ou hybride réel + prévision
     · Éditer réel — import Excel, saisie, notes, ajustements (vue Réel)
     · Planifier   — le bilan prévisionnel piloté : écoulements, drivers,
                     lignes de planification (vue Prévisionnel)

   Contrairement au compte de résultat (UN composant, trois modes), le bilan a
   DEUX composants — le réel (BalanceSheetPage) et le prévisionnel piloté
   (ForecastBalanceSheetTab). La barre choisit lequel monter ; aucun des deux
   n'est modifié. */
export default function BalanceSheetCommandPage({ activeTenant, addToast, year, onYearChange }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [vue, setVue] = usePersistentValue(`bs_vue_${tid}`, 'reel');
  const [mode, setMode] = usePersistentValue(`bs_mode_${tid}`, 'present');
  const [unit, setUnit] = usePersistentValue(`bs_cmd_unit_${tid}`, 'kchf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme partout ailleurs (2026-09-18) :
     elle tenait la droite de la ligne des modes, où elle changeait de place
     d'un écran à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  // Hôte des outils du bilan réel (Importer, ✏️ Éditer, niveau…) : ils se
  // téléportent sur la MÊME rangée que les pilules de mode, comme au compte
  // de résultat. Callback ref → l'hôte existe avant le rendu des outils.
  const [toolsHost, setToolsHost] = useState(null);

  // Un mode entraîne sa vue : planifier regarde l'avenir, éditer le réel.
  useEffect(() => {
    if (mode === 'plan') setVue('prev');
    if (mode === 'edit') setVue('reel');
    // eslint-disable-next-line
  }, [mode]);

  // Changer de vue à contre-sens d'un mode d'écriture ramène en lecture :
  // on n'édite pas le réel en regardant la prévision, ni l'inverse.
  const changeVue = (v) => {
    setVue(v);
    if ((mode === 'edit' && v === 'prev') || (mode === 'plan' && v === 'reel')) setMode('present');
  };

  const body = mode === 'plan' ? (
    <ForecastBalanceSheetTab activeTenant={activeTenant} year={year} unit={unit} />
  ) : mode === 'edit' ? (
    <BalanceSheetPage embedded actualsOnly unit={unit} toolsHost={toolsHost}
      activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />
  ) : vue === 'prev' ? (
    <BalanceSheetPage embedded actualsOnly monthsFull flatForecast readOnly unit={unit}
      toolsHost={toolsHost} activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />
  ) : (
    <BalanceSheetPage embedded actualsOnly readOnly unit={unit} toolsHost={toolsHost}
      activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />
  );

  return (
    <PageShell framed kind="report" unit={pageUnit} title={p('Bilan')}>
      <nav className="tabs">
        <div className="tabs__list">
          <ViewSwitch
            ariaLabel={p('Vue')}
            value={vue}
            onChange={changeVue}
            options={[{ id: 'reel', label: p('Réel') }, { id: 'prev', label: p('Prévisionnel') }]}
          />
        </div>
      </nav>
      <div className="pl-cmdbar__row">
        <ViewSwitch
          ariaLabel={p('Mode')}
          value={mode}
          onChange={setMode}
          options={[
            { id: 'present', label: p('Présenter') },
            { id: 'edit', label: p('Éditer réel') },
            { id: 'plan', label: p('Planifier') },
          ]}
        />
        <span className="pl-cmdbar__ctx" ref={setToolsHost} />
      </div>
      <div className="page-shell__panel">{body}</div>
    </PageShell>
  );
}
